import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import RedoIcon from '@mui/icons-material/Redo';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import { Button as ButtonMUI, Grid, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { IDepartmentInputModel, ITransferCollaboratorInputModel } from 'inputModels';
import { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useForm } from 'react-hook-form';
import { ICollaboratorViewModel, IDepartmentViewModel } from 'viewModels';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Button, Input, MenuOptionLeftCompany, NavHeader } from '../../components';
import { BasicButton } from '../../components/Buttons/BasicButton';
//import ButtonImportXLSX from '../../components/ButtonImportXLSX';
import { CardDepartment } from '../../components/Cards/CardDepartment';
import { ModalSeeCollaborators } from '../../components/Modals/ModalSeeCollaborators';
import { useApp, useAuth, useDepartments } from '../../context';
import { CompanyController } from '../../controller/CompanyController';
//import { getTemplate } from '../../controller/ReportController';
import { ItemType, TutorialType } from '../../types/enums';
import { Body, Container, Content } from './styles';
import { useAlert } from '../../hooks/useAlert';
import { useCollaborator } from '../../context/collaborator';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';

export function Department() {
  const app = useApp();
  const alert = useAlert();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { setCollaborators } = useCollaborator();
  const { departments, setDepartments, isFetchingDepartments } = useDepartments();
  const [nameDepartment, setNameDepartment] = useState('');
  const [fromDrop, setFromDrop] = useState<IDepartmentViewModel>();
  const [isTransferActive, setIsTransferActive] = useState(false);
  const [toDrop, setToDrop] = useState<IDepartmentViewModel>();
  const [isOpenModalSeeCollaborators, setIsOpenModalSeeCollaborators] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState<IDepartmentViewModel>();
  const [isDepartmentDropExists, setIsDepartmentDropExists] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const departmentToTutorial: IDepartmentViewModel[] = [
    {
      id: uuid(),
      name: 'Departamento Tutorial',
      isActive: true,
      collaborators: [
        {
          id: uuid(),
          firstName: 'Colaborador Tutorial 1',
          email: 'colaborador@tutorial.com.br',
          companyId: uuid(),
          departmentId: uuid(),
          departmentName: 'Departamento Tutorial',
        },
      ],
    },
  ];
  const steps = [
    {
      target: '#start-item',
      content:
        'Os departamentos correspondem as divisões da sua empresa, a partir desse menu é possível adicionar novos departamentos, editar ou visualizar os departamentos já cadastrados.',
      disableBeacon: true,
    },
    {
      target: '#add-new-department',
      content:
        'Para cadastrar um novo departamento basta preencher o campo de nome e clicar em adicionar.',
    },
    {
      target: '#search-department',
      content:
        'Para buscar um departamento basta digitar o nome do departamento no campo de busca.',
    },
    {
      target: '#transfer-button',
      content: `Clique neste botão para habilitar a opção de transferir colaboradores de um departamento para outro.`,
    },
    {
      target: '#origin-department',
      content: 'Arraste para cá o departamento de onde deseja transferir os colaboradores.',
    },
    {
      target: '#destination-department',
      content: 'Arraste para cá o departamento que receberá os colaboradores.',
    },
    {
      target: '#transfer-confirm',
      content:
        'Após arrastar os departamentos, clique no botão "Transferir" para realizar a transferência dos colaboradores.',
    },
    {
      target: '#disable-department',
      content:
        'Para alterar o status de um departamento, clique no botão correspondente para ativá-lo ou desativá-lo. Ao desativar um departamento, você não poderá adicionar colaboradores nele e os colaboradores que fazem parte dele.',
    },
  ];
  const defaultDepartment = {
    departmentName: '',
  };
  const [, fromDropRef] = useDrop({
    accept: ItemType.DEPARTMENT,
    drop: (item: IDepartmentViewModel) => {
      if (item.id === toDrop?.id) {
        setIsDepartmentDropExists(true);
        return;
      }
      setFromDrop(item);
      setIsDepartmentDropExists(false);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [, toDropRef] = useDrop({
    accept: ItemType.DEPARTMENT,
    drop: (item: IDepartmentViewModel) => {
      if (item.id === fromDrop?.id) {
        setIsDepartmentDropExists(true);
        return;
      }
      setToDrop(item);
      setIsDepartmentDropExists(false);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const departmentSchema = yup.object().shape({
    departmentName: yup
      .string()
      .required('O campo nome é obrigatório')
      .min(2, 'O campo nome deve ter no mínimo 2 caracteres'),
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setError,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: defaultDepartment,
    resolver: yupResolver(departmentSchema),
  });

  const addDepartment = async () => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! O departamento não será cadastrada.',
        type: 'success',
      });
      return;
    }
    if (!user?.id) return;
    if (departments.map((d) => d.name).includes(getValues('departmentName'))) {
      setError('departmentName', { message: 'Já existe este departamento' });
      return;
    }
    app.setLoading(true);
    const department: IDepartmentInputModel = {
      name: getValues('departmentName'),
      userId: user.id,
    };

    await CompanyController.addDepartment(department)
      .then((id) => {
        reset({
          departmentName: '',
        });
        alert.show({ message: 'Departamento adicionado com sucesso', type: 'success' });
        setDepartments((prev) => [
          ...prev,
          { ...department, isActive: true, id, collaborators: [] },
        ]);
      })
      .catch(() => {
        alert.show({ message: 'Erro ao adicionar departamento', type: 'error' });
      })
      .finally(() => {
        app.setLoading(false);
      });
  };

  const handleTransferCollaborator = async () => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! Os colaboradores não serão transferidos.',
        type: 'success',
      });
    }
    if (fromDrop && toDrop) {
      const trasnfer: ITransferCollaboratorInputModel = {
        fromId: fromDrop.id,
        toId: toDrop.id,
      };
      await CompanyController.transferCollaborators(trasnfer).then(() => {
        setFromDrop(undefined);
        setToDrop(undefined);
        setDepartments((prev) => {
          const newDepartments = [...prev];
          const fromIndex = prev.findIndex((d) => d.id === fromDrop?.id);
          const toIndex = prev.findIndex((d) => d.id === toDrop?.id);
          if (fromIndex !== -1 && toIndex !== -1) {
            newDepartments[toIndex] = {
              ...newDepartments[toIndex],
              collaborators: [
                ...newDepartments[toIndex].collaborators,
                ...newDepartments[fromIndex].collaborators,
              ],
            };
            newDepartments[fromIndex] = {
              ...newDepartments[fromIndex],
              collaborators: [],
            };
            setCollaborators((prevCollaborators) => {
              const newCollaborators: ICollaboratorViewModel[] = prevCollaborators.map((c) => {
                const newCollaborator = { ...c };

                if (newCollaborator.departmentId === fromDrop.id) {
                  newCollaborator.departmentId = toDrop.id;
                  newCollaborator.departmentName = toDrop.name;
                }
                return newCollaborator;
              });
              return newCollaborators;
            });
          }
          return newDepartments;
        });
        alert.show({ message: 'Colaboradores transferidos com sucesso', type: 'success' });
      });
    }
  };

  const handleSelectCurrentDepartment = (department: IDepartmentViewModel) => {
    setCurrentDepartment(department);
    setIsOpenModalSeeCollaborators(true);
  };
  const handleUpdateDepartment = async () => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! O departamento não será atualizado.',
        type: 'success',
      });
    }
  };

  useEffect(() => {
    app.setLoading(isFetchingDepartments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingDepartments]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.DEPARTMENT)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <JoyrideTour
        type={TutorialType.DEPARTMENT}
        steps={steps}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <Body>
        <MenuOptionLeftCompany />
        <Container>
          <TitlePage
            title="Departamentos"
            onRequestOpen={() => setRunningTutorial(true)}
            id="start-item"
          />
          <Content>
            <Input
              name="departmentName"
              placeholder="Nome"
              className="clientName"
              control={control}
              variant="standard"
              errorText={errors.departmentName?.message}
            />
            <div className="add">
              <Button
                text="Adicionar"
                id="add-new-department"
                dimensions={{ width: '13rem', height: '2.25rem' }}
                onClick={handleSubmit(addDepartment)}
              />
            </div>
          </Content>
          <>
            <div className="cnt-departments">
              <div className="departments-header">
                <div className="transfer">
                  <span>Cadastrados</span>
                  <button
                    type="button"
                    className="transferButton"
                    id="transfer-button"
                    onClick={() => setIsTransferActive(true)}>
                    <div className="icon-transfer">
                      <RedoIcon />
                    </div>
                    <span className="text-transfer">Transferir colaboradores</span>
                  </button>
                </div>

                <div className="filter" id="search-department">
                  <TextField
                    id="standard-basic"
                    label="Buscar"
                    variant="standard"
                    className="text-field"
                    onChange={(e) => setNameDepartment(e.target.value)}
                  />
                  <SearchIcon className="icon-search" />
                </div>
              </div>
            </div>
            {(runningTutorial || isTransferActive) && (
              <div className="cnt-transfer">
                <div className="transfer-header">
                  <div className="button-close">
                    <ButtonMUI
                      onClick={() => {
                        setIsTransferActive(false);
                        setFromDrop(undefined);
                        setToDrop(undefined);
                      }}>
                      <span>Fechar</span>
                      <CloseIcon />
                    </ButtonMUI>
                  </div>
                </div>
                <div className="transfer">
                  <div className="containerTransfers">
                    <div className="transfer-primary">
                      <div>
                        <p className="title">Transferir desse</p>
                        <div ref={fromDropRef} className="transfer-dragdrop" id="origin-department">
                          {(fromDrop && (
                            <div style={{ width: '100%' }}>
                              <CardDepartment
                                id={fromDrop.id}
                                name={fromDrop.name}
                                runningTutorial={runningTutorial}
                                onUpdateDepartment={handleUpdateDepartment}
                                isActivated={fromDrop.isActive}
                                isDragabble
                                width={100}
                              />
                            </div>
                          )) || <p>Arraste o departamento</p>}
                        </div>
                      </div>
                    </div>
                    <div className="icon-transfer">
                      <RedoIcon />
                    </div>
                    <div className="transfer-secondary">
                      <div>
                        <p className="title">Para esse</p>
                        <div
                          ref={toDropRef}
                          className="transfer-dragdrop"
                          id="destination-department">
                          {(toDrop && (
                            <div style={{ width: '100%' }}>
                              <CardDepartment
                                id={toDrop.id}
                                name={toDrop.name}
                                isActivated={toDrop.isActive}
                                runningTutorial={runningTutorial}
                                onUpdateDepartment={handleUpdateDepartment}
                                isDragabble
                                width={100}
                              />
                            </div>
                          )) || <p>Arraste o departamento</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="containerButtonTransfer" style={{ alignSelf: 'self-end' }}>
                    <div className="button-transfer">
                      <BasicButton
                        variant="outlined"
                        size="small"
                        id="transfer-confirm"
                        onClick={handleTransferCollaborator}>
                        Transferir
                      </BasicButton>
                    </div>
                  </div>
                </div>
                {isDepartmentDropExists && (
                  <span className="fieldError">
                    <ErrorIcon fontSize="inherit" /> Não é possível escolher o mesmo departamento
                  </span>
                )}
              </div>
            )}
            <div className="cnt-departments-created">
              {runningTutorial &&
                departmentToTutorial
                  .filter((d) => d.name.toLowerCase().includes(nameDepartment.toLocaleLowerCase()))
                  .map((department) => (
                    <CardDepartment
                      id={department.id}
                      name={department.name}
                      isActivated={department.isActive}
                      isDragabble={false}
                      key={department.id}
                      runningTutorial={runningTutorial}
                      onUpdateDepartment={handleUpdateDepartment}
                      onCallback={() => handleSelectCurrentDepartment(department)}
                    />
                  ))}
              {!runningTutorial && departments.length > 0 ? (
                departments
                  .filter((d) => d.name.toLowerCase().includes(nameDepartment.toLocaleLowerCase()))
                  .map((department) => (
                    <CardDepartment
                      id={department.id}
                      name={department.name}
                      isActivated={department.isActive}
                      isDragabble={false}
                      key={department.id}
                      onUpdateDepartment={handleUpdateDepartment}
                      runningTutorial={runningTutorial}
                      onCallback={() => handleSelectCurrentDepartment(department)}
                    />
                  ))
              ) : (
                <Grid container padding="2rem" justifyContent="center">
                  <Typography justifyContent="center" align="center" color={colors.primary}>
                    Nenhum departamento encontrado...
                  </Typography>
                </Grid>
              )}
            </div>
          </>
        </Container>
        <ModalSeeCollaborators
          isOpen={isOpenModalSeeCollaborators}
          department={currentDepartment as IDepartmentViewModel}
          onRequestClose={() => setIsOpenModalSeeCollaborators(false)}
        />
      </Body>
    </>
  );
}
